

// Auto-gift product functionality
class AutoGiftManager {
  constructor() {
    
    
    this.settings = window.themeVariables?.settings || {};
    this.autoGiftEnabled = this.settings.cartAutoGiftEnabled === true || this.settings.cartAutoGiftEnabled === 'true';
    this.threshold = parseInt(this.settings.cartAutoGiftThreshold) || 8000;
    this.giftProductId = this.settings.cartAutoGiftProduct;
    this.giftProductHandle = this.settings.cartAutoGiftProductHandle;
    this.giftFirstAvailableVariantId = this.settings.cartAutoGiftProductFirstAvailableVariantId;
    this.giftFirstVariantId = this.settings.cartAutoGiftProductFirstVariantId;
    this.giftMessage = this.settings.cartAutoGiftMessage || 'Free gift added to your cart!';
    this.giftBadgeText = this.settings.cartAutoGiftBadgeText || 'Free Gift';
    this.progressUnreachedText = this.settings.cartAutoGiftProgressUnreachedText || 'Add {{ amount }} more to get your free gift!';
    this.progressReachedText = this.settings.cartAutoGiftProgressReachedText || '🎉 You\'ve earned a free gift!';
    this.isProcessing = false;
    this.debounceTimer = null;
    this.lastActionTime = 0;
    this.operationQueue = Promise.resolve(); // Queue for sequential operations
    
    
    
    // Check if we have required settings
    if (!this.autoGiftEnabled) {
      
      return;
    }
    
    if (!this.giftProductId) {
      
      return;
    }
    
    
    this.init();
  }

  init() {
    
    
    // Add multiple event listeners for comprehensive coverage
    document.addEventListener('cart:change', (event) => {
      
      this.handleCartChange(event);
    });
    
    document.addEventListener('variant:add', (event) => {
      
      this.handleCartChange(event);
    });
    
    document.addEventListener('DOMContentLoaded', () => {
      
      setTimeout(() => this.checkCartForAutoGift(), 500);
    });
    
    // Also check immediately and after a delay for various initialization timings
    setTimeout(() => {
      
      this.checkCartForAutoGift();
    }, 1000);
    
    setTimeout(() => {
      
      this.checkCartForAutoGift();
    }, 3000);
    
    
  }

  async handleCartChange(event) {
    if (this.isProcessing) {
      
      return;
    }
    
    // Skip if this event was triggered by auto-gift itself
    if (event.detail?.baseEvent === 'auto-gift:add' || event.detail?.baseEvent === 'auto-gift:remove') {
      
      return;
    }
    
    
    const cart = event.detail?.cart;
    
    if (!cart) {
      
      await this.checkCartForAutoGift();
    } else {
      
      await this.checkCartForAutoGift(cart);
    }
  }

  async checkCartForAutoGift(cartData = null) {
    // Enqueue this operation to ensure sequential execution
    this.operationQueue = this.operationQueue.then(async () => {
      if (this.isProcessing) {
        
        return;
      }
      
      // Debounce: prevent rapid successive checks
      const now = Date.now();
      if (now - this.lastActionTime < 500) {
        
        return;
      }
      
      
      
      return this._executeCartCheck(cartData);
    }).catch(error => {
      
    });
    
    return this.operationQueue;
  }
  
  async _executeCartCheck(cartData = null) {
    
    try {
      // Get current cart if not provided
      let cart;
      if (cartData) {
        cart = cartData;
        
      } else {
        
        const response = await fetch(`${Shopify.routes.root}cart.js`);
        cart = await response.json();
        
      }
      
      const cartTotal = cart.total_price;
      const hasGiftProduct = this.hasGiftProductInCart(cart);
      const shouldAdd = cartTotal >= this.threshold && !hasGiftProduct;
      const shouldRemove = cartTotal < this.threshold && hasGiftProduct;
      
      
      
      if (shouldAdd) {
        
        // Double-check processing lock before proceeding
        if (this.isProcessing) {
          
          return;
        }
        await this.addGiftProduct();
      } else if (shouldRemove) {
        
        // Double-check processing lock before proceeding
        if (this.isProcessing) {
          
          return;
        }
        await this.removeGiftProduct(cart);
      } else {
        
      }
    } catch (error) {
      
    }
  }

  hasGiftProductInCart(cart) {
    
    
    const giftItems = cart.items.filter(item => {
      const isGiftProduct = item.product_id.toString() === this.giftProductId.toString();
      const isAutoGift = item.properties && item.properties._auto_gift === 'true';
      
      
      
      return isGiftProduct && isAutoGift;
    });
    
    const hasGift = giftItems.length > 0;
    
    
    // Additional check: if we find ANY instance of the gift product (even without auto_gift tag), 
    // we should also consider it as present to avoid adding duplicates
    const allGiftProducts = cart.items.filter(item => 
      item.product_id.toString() === this.giftProductId.toString()
    );
    
    if (allGiftProducts.length > giftItems.length) {
      
      
      return true;
    }
    
    return hasGift;
  }

  async addGiftProduct() {
    if (this.isProcessing) {
      
      return;
    }
    
    // Set processing lock IMMEDIATELY before any async operations
    
    
    this.isProcessing = true;

    try {
      // Double-check the cart right before adding to prevent race conditions
      
      const preCheckResponse = await fetch(`${Shopify.routes.root}cart.js`);
      const preCheckCart = await preCheckResponse.json();
      
      const allGiftProducts = preCheckCart.items.filter(item => 
        item.product_id.toString() === this.giftProductId.toString()
      );
      
      if (allGiftProducts.length > 0) {
        
        this.isProcessing = false;
        return;
      }
      
      
      
      // Prefer variant id exposed from Liquid to avoid extra fetch
      let selectedVariantId = this.giftFirstAvailableVariantId || this.giftFirstVariantId;
      let selectedVariantInfo = null;
      
      if (selectedVariantId) {
        
      } else {
        
        const handlePath = this.giftProductHandle ? this.giftProductHandle : this.giftProductId; // fallback
        const productUrl = `${Shopify.routes.root}products/${handlePath}.js`;
        
        
        try {
          const productResponse = await fetch(productUrl);
          if (!productResponse.ok) {
            
            return;
          }
          const product = await productResponse.json();
          
          
          if (!product || !product.variants || product.variants.length === 0) {
            
            return;
          }
          
          const availableVariant = product.variants.find(v => v.available) || product.variants[0];
          selectedVariantId = availableVariant.id;
          selectedVariantInfo = availableVariant;
        } catch(fetchErr) {
          
          return;
        }
      }
      
      if (!selectedVariantId) {
        
        return;
      }
      
      if (selectedVariantInfo) {
        
      }
      
      // Prepare cart add data
      const addData = {
        id: selectedVariantId,
        quantity: 1,
        properties: {
          '_auto_gift': 'true',
          '_gift_message': this.giftMessage
        }
      };
      
      
      
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addData)
      });

      let addResult = null;
      try {
        addResult = await addResponse.json();
      } catch(jsonErr) {
        
      }
      
      

      if (addResponse.ok) {
        
        this.lastActionTime = Date.now();
        
        // Note: Gift notification removed as requested
        
        // Trigger comprehensive cart refresh with proper sections
        
        
        // Get updated cart and sections data (following theme's pattern)
        const sectionsToBundle = [];
        
        // Dispatch event to collect sections that need to be updated
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { 
          bubbles: true, 
          detail: { sections: sectionsToBundle } 
        }));
        
        
        
        // Fetch basic cart data
        const updatedCartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const updatedCart = await updatedCartResponse.json();
        
        
        // Fetch sections data if needed
        if (sectionsToBundle.length > 0) {
          try {
            const sectionsUrl = `${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`;
            
            
            const sectionsResponse = await fetch(sectionsUrl);
            const sectionsData = await sectionsResponse.text();
            
            // Parse sections data - the response contains JSON with section HTML
            let sections = {};
            try {
              // Try parsing as JSON first (Shopify's section endpoint returns JSON)
              const sectionsJson = JSON.parse(sectionsData);
              sections = sectionsJson;
              
            } catch (jsonParseError) {
              
              // Fallback: parse as HTML
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = sectionsData;
              
              sectionsToBundle.forEach(sectionId => {
                const sectionElement = tempDiv.querySelector(`#shopify-section-${sectionId}`);
                if (sectionElement) {
                  sections[sectionId] = sectionElement.outerHTML;
                }
              });
            }
            
            // Add sections to cart object
            updatedCart.sections = sections;
            
          } catch (sectionsError) {
            
          }
        }
        
        // Dispatch events with proper sections data
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "auto-gift:add",
            cart: updatedCart
          }
        }));
        
        
      } else {
        
      }
    } catch (error) {
      
    } finally {
      // Delay releasing the lock to ensure all cascade events complete
      setTimeout(() => {
        this.isProcessing = false;
        
      }, 1000);
    }
  }

  async removeGiftProduct(cart) {
    if (this.isProcessing) {
      
      return;
    }
    
    
    this.isProcessing = true;

    try {
      const giftItem = cart.items.find(item => 
        item.product_id.toString() === this.giftProductId.toString() && 
        item.properties && 
        item.properties._auto_gift === 'true'
      );

      if (!giftItem) {
        
        return;
      }

      
      
      const removeData = {
        id: giftItem.key,
        quantity: 0
      };
      
      
      
      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(removeData)
      });

      const removeResult = await removeResponse.json();
      

      if (removeResponse.ok) {
        
        this.lastActionTime = Date.now();
        
        // Trigger cart refresh with proper sections
        const sectionsToBundle = [];
        
        // Dispatch event to collect sections that need to be updated
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { 
          bubbles: true, 
          detail: { sections: sectionsToBundle } 
        }));
        
        
        
        // Fetch basic cart data
        const updatedCartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const updatedCartResult = await updatedCartResponse.json();
        
        // Fetch sections data if needed
        if (sectionsToBundle.length > 0) {
          try {
            const sectionsUrl = `${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`;
            const sectionsResponse = await fetch(sectionsUrl);
            const sectionsData = await sectionsResponse.text();
            
            // Parse sections data - the response contains JSON with section HTML
            let sections = {};
            try {
              // Try parsing as JSON first (Shopify's section endpoint returns JSON)
              const sectionsJson = JSON.parse(sectionsData);
              sections = sectionsJson;
              
            } catch (jsonParseError) {
              
              // Fallback: parse as HTML
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = sectionsData;
              
              sectionsToBundle.forEach(sectionId => {
                const sectionElement = tempDiv.querySelector(`#shopify-section-${sectionId}`);
                if (sectionElement) {
                  sections[sectionId] = sectionElement.outerHTML;
                }
              });
            }
            
            // Add sections to cart object
            updatedCartResult.sections = sections;
            
          } catch (sectionsError) {
            
          }
        }
        
        // Dispatch cart change event with proper sections data
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "auto-gift:remove",
            cart: updatedCartResult
          }
        }));
      } else {
        
      }
    } catch (error) {
      
    } finally {
      // Delay releasing the lock to ensure all cascade events complete
      setTimeout(() => {
        this.isProcessing = false;
        
      }, 1000);
    }
  }

  showGiftNotification() {
    
    
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = 'auto-gift-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">🎁</span>
        <span class="notification-message">${this.giftMessage}</span>
      </div>
    `;
    
    // Add some basic styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
      animation: slideInRight 0.3s ease-out;
    `;

    // Add CSS animation if not already present
    if (!document.getElementById('auto-gift-styles')) {
      const style = document.createElement('style');
      style.id = 'auto-gift-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .notification-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .notification-icon {
          font-size: 18px;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    // Remove notification after 4 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideInRight 0.3s ease-out reverse';
        setTimeout(() => {
          notification.remove();
        }, 300);
      }
    }, 4000);
  }
}

// Initialize auto-gift manager with comprehensive debugging


// Global flag to ensure only ONE instance is created
let autoGiftManagerInstance = null;

function initializeAutoGift() {
  // Prevent multiple instances
  if (autoGiftManagerInstance) {
    
    return;
  }
  
  
  
  
  if (typeof Shopify === 'undefined') {
    
    setTimeout(initializeAutoGift, 500);
    return;
  }
  
  if (!window.themeVariables) {
    
    setTimeout(initializeAutoGift, 500);
    return;
  }
  
  
  autoGiftManagerInstance = new AutoGiftManager();
  
}

if (document.readyState === 'loading') {
  
  document.addEventListener('DOMContentLoaded', initializeAutoGift);
} else {
  
  initializeAutoGift();
}

// Also try initialization after a delay to catch any timing issues
setTimeout(() => {
  
  initializeAutoGift();
}, 2000);


